import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  ChartBarBig,
  ArrowUpRight,
  Check,
  CheckCheck,
  Clock3,
  Copy,
  CornerUpLeft,
  Globe,
  Info,
  MapPin,
  MessageCircle,
  Mic,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Forward,
  SquareKanban,
  ChevronsRight,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocale } from "@/hooks/useLocale";
import { cn } from "@/utils/cn";
import type {
  IChatLocalMessage,
  IChatMessage,
  IChatPoll,
} from "@/types/(my-space)/chat.interface";
import { ChatAvatar } from "./ChatAvatar";
import { CardAction, CardTile, FileCard, RichCard } from "./ChatCard";
import { ChatLocationMap } from "./ChatLocationMap";
import { VideoTile } from "./VideoTile";
import { VoiceNotePlayer } from "./VoiceNotePlayer";
import { ShortcutCard } from "./ShortcutCard";
import { MessageText } from "./MessageText";
import { NoticeMessage } from "./NoticeMessage";
import { PollVotesDialog } from "./PollVotesDialog";
import { ReactionButton, ReactionPill } from "./MessageReactions";
import {
  messageContentPreview,
  personName,
  systemMessageText,
} from "../_lib/chat-labels";
import { mapsUrl } from "../_lib/chat-utils";
import { canReactTo } from "../_lib/reactions";
import type { TickState } from "../_lib/ticks";
import { isUnplayedByMe } from "../_lib/voice-played";
import { canForward, forwardedKind } from "../_lib/forward";
import { jumboEmojiCount } from "../_lib/emoji-text";

export interface MessageBubbleProps {
  message: IChatLocalMessage;
  isMine: boolean;
  showSender: boolean;
  /**
   * Group chats: a column for senders' pictures beside incoming messages.
   * A direct chat has no one else to tell apart, so no empty gutter.
   */
  avatarColumn?: boolean;
  /** Inside a floating chat window: tighter edges, wider bubbles. */
  compact?: boolean;
  /** Own messages: pending / sent / delivered to all / read by all. */
  tick: TickState;
  myUserId: number | undefined;
  memberNames: Map<number, string>;
  /** Profile pictures for the "who reacted" list. */
  memberImages?: Map<number, string | null>;
  canReply: boolean;
  /** I can post here (a broadcast's recipients can't) — reacting needs it too. */
  canReact: boolean;
  canShowInfo: boolean;
  /**
   * Who voted what can be shown: I can see the other members (a broadcast's
   * recipients can't see each other).
   */
  canSeeVoters: boolean;
  /** My voice note: every other member it was meant for has played it. */
  voicePlayed: boolean;
  /** I started playing this voice note. */
  onPlayed: (m: IChatMessage) => void;
  onReply: (m: IChatMessage) => void;
  onEdit: (m: IChatMessage) => void;
  onDelete: (m: IChatMessage) => void;
  onInfo: (m: IChatMessage) => void;
  onRetry: (m: IChatLocalMessage) => void;
  onDiscard: (m: IChatLocalMessage) => void;
  onVote: (m: IChatMessage, optionId: number) => void;
  /** Picks a reaction; my current one takes it back. */
  onReact: (m: IChatMessage, emoji: string) => void;
  onMessageUser: (userId: number) => void;
  /** Opens the app-wide image previewer on `urls[index]`. */
  onOpenImage: (urls: string[], index: number) => void;
  onCopy: (text: string) => void;
  /** Shows the quoted message (reply quote clicked). */
  onJumpToMessage?: (messageId: number) => void;
  /** I may pin here (whoever can write: not a broadcast's recipients). */
  canPin: boolean;
  /** Pins it for everyone, or unpins it. */
  onTogglePin: (m: IChatMessage) => void;
  /** Opens the forward picker for it. */
  onForward: (m: IChatMessage) => void;
  /** "Create ticket": starts picking messages for a project ticket, with this one. */
  onCreateTicket?: (m: IChatMessage) => void;
  /** Picking messages (for a ticket): a click toggles this one; menus hide. */
  selecting?: boolean;
  selected?: boolean;
  onToggleSelect?: (m: IChatMessage) => void;
  /** The sender's picture or name was clicked: their profile popup. */
  onOpenSender?: (sender: NonNullable<IChatMessage["sender"]>) => void;
}

/** Messages drawn as pictures/videos filling the bubble. */
const MEDIA_TYPES = new Set<string>(["image", "video", "gif"]);

export const MessageBubble = memo(function MessageBubble(props: MessageBubbleProps) {
  const { message: m, isMine, showSender, memberNames, myUserId } = props;
  const { t } = useTranslation("chat");

  if (m.type === "system" && m.metadata?.system) {
    const system = m.metadata.system;
    const text = systemMessageText(t, system, { names: memberNames, myUserId });
    const pinnedId = system.event === "message_pinned" ? system.messageId : undefined;
    const pill = "max-w-[85%] rounded-full bg-muted px-3 py-1 text-center text-xs text-muted-foreground";
    return (
      <div className="flex justify-center px-4 py-1.5">
        {pinnedId && props.onJumpToMessage ? (
          // "X pinned a message" shows that message.
          <button
            type="button"
            onClick={() => props.onJumpToMessage?.(pinnedId)}
            className={cn(pill, "inline-flex items-center gap-1.5 hover:bg-muted/70")}
          >
            <Pin className="size-3 shrink-0" />
            {text}
          </button>
        ) : (
          <span className={pill}>{text}</span>
        )}
      </div>
    );
  }

  // An app channel's update: a card of its own, not a bubble.
  if (m.type === "notice") {
    return <NoticeMessage message={m} compact={props.compact} />;
  }

  const failed = m.sendState === "failed";
  const forwarded = forwardedKind(m);
  // Like WhatsApp: 1–3 emoji alone are drawn big, without a bubble.
  const jumbo =
    m.type === "text" && !m.isDeleted && !m.replyTo && !forwarded ? jumboEmojiCount(m.body) : 0;
  const isMedia = MEDIA_TYPES.has(m.type) && !m.isDeleted;
  // A lone picture/video: its own rounded corners are the bubble — no frame.
  const frameless =
    (isMedia && !m.body?.trim() && !m.replyTo && !(showSender && !isMine) && !forwarded) || jumbo > 0;
  // Where the time and ticks go.
  const footerVariant: FooterVariant = jumbo
    ? "pill"
    : isMedia && !m.body?.trim()
    ? "overlay"
    : m.type === "text" && !m.isDeleted
      ? "floating"
      : m.type === "voice" && !m.isDeleted
        ? "none"
        : "below";
  const pending = m.id < 0;
  const senderName = personName(t, m.sender);
  const selectable = !!props.selecting && isTicketSource(m);

  return (
    <div
      className={cn(
        "group relative flex items-end gap-2 py-0.5",
        props.compact ? "px-2.5" : "px-3 md:px-5",
        isMine ? "flex-row-reverse" : "flex-row",
        props.selecting && "ps-11 md:ps-12",
        props.selected && "bg-primary/10",
      )}
    >
      {props.selecting ? (
        // The whole row toggles it; nothing inside (links, players) reacts meanwhile.
        <button
          type="button"
          disabled={!selectable}
          onClick={() => props.onToggleSelect?.(m)}
          aria-pressed={!!props.selected}
          aria-label={props.selected ? t("ticket_from_chat.unselect") : t("ticket_from_chat.select")}
          className="absolute inset-0 z-10 flex items-center ps-3 md:ps-4 disabled:cursor-default"
        >
          {selectable ? (
            <span
              className={cn(
                "flex size-5 items-center justify-center rounded-full border-2 transition-colors",
                props.selected
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-muted-foreground/50 bg-background",
              )}
            >
              {props.selected ? <Check className="size-3" strokeWidth={3} /> : null}
            </span>
          ) : null}
        </button>
      ) : null}
      {!isMine && (props.avatarColumn ?? true) ? (
        <div className="w-8 shrink-0">
          {showSender ? (
            m.sender && props.onOpenSender ? (
              <button
                type="button"
                onClick={() => props.onOpenSender?.(m.sender!)}
                aria-label={t("profile.open", { name: senderName })}
                className="rounded-full focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
              >
                <ChatAvatar name={senderName} imageUrl={m.sender.profileImage} size="sm" />
              </button>
            ) : (
              <ChatAvatar name={senderName} imageUrl={m.sender?.profileImage} size="sm" />
            )
          ) : null}
        </div>
      ) : null}

      <div
        className={cn(
          "flex flex-col",
          props.compact ? "max-w-[85%]" : "max-w-[78%] md:max-w-[65%]",
          isMine ? "items-end" : "items-start",
        )}
      >
        <div
          className={cn(
            // Big emoji have no bubble to clip to (it would cut the name and time).
            jumbo ? "relative text-sm" : "relative min-w-[84px] overflow-hidden rounded-2xl text-sm",
            // Media fill the bubble inside a slim frame; text gets the usual padding.
            frameless ? "p-0" : isMedia ? "p-1 shadow-xs" : "px-3 py-2 shadow-xs",
            frameless
              ? isMine
                ? "rounded-ee-md text-primary-foreground"
                : "rounded-es-md text-foreground"
              : isMine
                ? "rounded-ee-md bg-primary text-primary-foreground"
                : "rounded-es-md border border-border bg-surface text-foreground",
            failed && "opacity-80 ring-1 ring-danger",
          )}
        >
          <div className={cn(isMedia && "px-2 pt-1 empty:hidden")}>
            {showSender && !isMine ? (
              m.sender && props.onOpenSender ? (
                <button
                  type="button"
                  onClick={() => props.onOpenSender?.(m.sender!)}
                  className="mb-0.5 block max-w-full truncate text-start text-xs font-semibold text-primary hover:underline"
                >
                  {senderName}
                </button>
              ) : (
                <div className="mb-0.5 truncate text-xs font-semibold text-primary">{senderName}</div>
              )
            ) : null}
            {forwarded && !m.isDeleted ? (
              // Like WhatsApp: a quiet line above the content.
              <div
                className={cn(
                  "mb-0.5 flex items-center gap-1 text-xs italic",
                  isMine ? "text-primary-foreground/75" : "text-muted-foreground",
                )}
              >
                {forwarded === "many" ? (
                  <ChevronsRight className="size-3.5 shrink-0 rtl-flip" />
                ) : (
                  <Forward className="size-3.5 shrink-0 rtl-flip" />
                )}
                {forwarded === "many" ? t("forward.forwarded_many") : t("forward.forwarded")}
              </div>
            ) : null}
            {m.replyTo ? (
              <ReplyQuote message={m} isMine={isMine} onJump={props.onJumpToMessage} />
            ) : null}
          </div>
          {jumbo ? (
            <p
              dir="auto"
              className={cn(
                "leading-none",
                jumbo === 1 ? "text-6xl" : jumbo === 2 ? "text-5xl" : "text-4xl",
              )}
            >
              {m.body?.trim()}
            </p>
          ) : (
            <BubbleContent {...props} />
          )}
          {footerVariant !== "none" ? (
            <BubbleFooter
              message={m}
              isMine={isMine}
              tick={props.tick}
              variant={footerVariant}
              className={isMedia ? "px-2 pb-1" : undefined}
            />
          ) : null}
        </div>

        <ReactionPill
          message={m}
          isMine={isMine}
          myUserId={myUserId}
          memberNames={memberNames}
          memberImages={props.memberImages}
          canReact={canReactTo(m, props.canReact)}
          onReact={props.onReact}
          className={isMine ? "me-2" : "ms-2"}
        />

        {failed ? (
          <div className="mt-1 flex items-center gap-1 text-xs text-danger">
            <AlertCircle className="size-3.5" />
            <span>{t("actions.not_sent")}</span>
            <Button variant="link" size="xs" className="h-auto p-0 text-xs" onClick={() => props.onRetry(m)}>
              <RotateCcw className="size-3" />
              {t("actions.retry")}
            </Button>
            <Button
              variant="link"
              size="xs"
              className="h-auto p-0 text-xs text-muted-foreground"
              onClick={() => props.onDiscard(m)}
            >
              <X className="size-3" />
              {t("actions.discard")}
            </Button>
          </div>
        ) : null}
      </div>

      {!pending && !m.isDeleted && !props.selecting ? <BubbleActions {...props} /> : null}
    </div>
  );
});

/**
 * Beside the bubble on hover: the reaction smiley (nearest the bubble) and the
 * "more" menu. Both stay shown while either is open.
 */
const BubbleActions = (props: MessageBubbleProps) => {
  const { message: m, isMine, canReact, myUserId, onReact } = props;
  const showReact = canReactTo(m, canReact);
  const menu = <BubbleMenu {...props} />;
  if (!showReact && !hasMenu(props)) return null;
  return (
    <div
      className={cn(
        "mb-1 flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 has-[[data-state=open]]:opacity-100",
        isMine && "flex-row-reverse",
      )}
    >
      {showReact ? (
        <ReactionButton message={m} isMine={isMine} myUserId={myUserId} onReact={onReact} />
      ) : null}
      {menu}
    </div>
  );
};

const menuFlags = ({ message: m, isMine, canPin, myUserId }: MessageBubbleProps) => ({
  canEdit: isMine && m.type === "text",
  canPin: canPin && m.id > 0 && !m.isDeleted && !m.sendState,
  canForward: canForward(m, myUserId),
  copyText: m.type === "text" ? m.body : m.type === "link" ? m.metadata?.link?.url : null,
});

/** A sent, live message with something to quote: can go into a ticket. */
const isTicketSource = (m: IChatMessage) =>
  m.id > 0 && !m.isDeleted && m.type !== "system" && m.type !== "notice" && m.type !== "poll";

const hasMenu = (props: MessageBubbleProps) => {
  const { canEdit, copyText, canPin, canForward } = menuFlags(props);
  return (
    props.canReply ||
    canEdit ||
    !!copyText ||
    canPin ||
    canForward ||
    props.isMine ||
    (!!props.onCreateTicket && isTicketSource(props.message))
  );
};

const BubbleMenu = (props: MessageBubbleProps) => {
  const { message: m, isMine, canReply, canShowInfo, onReply, onEdit, onDelete, onInfo, onCopy } =
    props;
  const { t } = useTranslation("chat");
  const { canEdit, copyText, canPin, canForward } = menuFlags(props);
  if (!hasMenu(props)) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={t("actions.more")}
          className="shrink-0"
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={isMine ? "end" : "start"}>
        {canReply ? (
          <DropdownMenuItem onSelect={() => onReply(m)}>
            <CornerUpLeft className="size-4" />
            {t("actions.reply")}
          </DropdownMenuItem>
        ) : null}
        {canForward ? (
          <DropdownMenuItem onSelect={() => props.onForward(m)}>
            <Forward className="size-4 rtl-flip" />
            {t("actions.forward")}
          </DropdownMenuItem>
        ) : null}
        {copyText ? (
          <DropdownMenuItem onSelect={() => onCopy(copyText)}>
            <Copy className="size-4" />
            {t("actions.copy")}
          </DropdownMenuItem>
        ) : null}
        {canEdit ? (
          <DropdownMenuItem onSelect={() => onEdit(m)}>
            <Pencil className="size-4" />
            {t("actions.edit")}
          </DropdownMenuItem>
        ) : null}
        {canPin ? (
          <DropdownMenuItem onSelect={() => props.onTogglePin(m)}>
            {m.pinnedAt ? <PinOff className="size-4" /> : <Pin className="size-4" />}
            {m.pinnedAt ? t("actions.unpin") : t("actions.pin")}
          </DropdownMenuItem>
        ) : null}
        {props.onCreateTicket && isTicketSource(m) ? (
          <DropdownMenuItem onSelect={() => props.onCreateTicket?.(m)}>
            <SquareKanban className="size-4" />
            {t("ticket_from_chat.menu")}
          </DropdownMenuItem>
        ) : null}
        {isMine && canShowInfo ? (
          <DropdownMenuItem onSelect={() => onInfo(m)}>
            <Info className="size-4" />
            {t("actions.message_info")}
          </DropdownMenuItem>
        ) : null}
        {isMine ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-danger focus:text-danger" onSelect={() => onDelete(m)}>
              <Trash2 className="size-4" />
              {t("actions.delete")}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ReplyQuote = ({
  message,
  isMine,
  onJump,
}: {
  message: IChatMessage;
  isMine: boolean;
  onJump?: (messageId: number) => void;
}) => {
  const { t } = useTranslation("chat");
  const r = message.replyTo!;
  const className = cn(
    "mb-1.5 block w-full rounded-md border-s-4 px-2 py-1 text-start text-xs",
    isMine ? "border-primary-foreground/70 bg-primary-foreground/15" : "border-primary bg-muted",
  );
  const content = (
    <>
      <span className="block truncate font-semibold">{r.senderName ?? t("system.someone")}</span>
      <span className={cn("line-clamp-2", isMine ? "opacity-90" : "text-muted-foreground")}>
        {messageContentPreview(t, r)}
      </span>
    </>
  );
  // A deleted original has nothing to show; neither has an unsent reply.
  if (!onJump || r.isDeleted || message.id < 0) {
    return <div className={className}>{content}</div>;
  }
  return (
    <button
      type="button"
      onClick={() => onJump(r.id)}
      title={t("search.go_to_quoted")}
      className={cn(
        className,
        "cursor-pointer transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden",
        isMine ? "hover:bg-primary-foreground/25" : "hover:bg-muted/70 hover:brightness-95",
      )}
    >
      {content}
    </button>
  );
};

/**
 * - below: its own line under the content
 * - overlay: a dark pill over a picture
 * - floating: pinned to the bubble's bottom-end, after text's last line
 * - ghost: an invisible copy at the end of the text that reserves that room
 *   (wraps to a new line only when the last line is too full)
 * - inline: part of a row (the voice player's)
 * - none: drawn elsewhere
 */
type FooterVariant = "below" | "overlay" | "floating" | "ghost" | "inline" | "pill" | "none";

const BubbleFooter = ({
  message: m,
  isMine,
  tick,
  variant = "below",
  className,
}: {
  message: IChatLocalMessage;
  isMine: boolean;
  tick: TickState;
  variant?: FooterVariant;
  className?: string;
}) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const muted = isMine ? "text-primary-foreground/75" : "text-muted-foreground";
  const tone = {
    below: cn("mt-1", muted, className),
    overlay:
      "absolute bottom-2.5 end-2.5 rounded-full bg-black/50 px-1.5 py-1 text-white backdrop-blur-xs",
    floating: cn("absolute bottom-2 end-3", muted),
    // Under a bubble-less big emoji: readable on any background.
    pill: "mt-1 ms-auto w-fit rounded-full bg-black/45 px-1.5 py-1 text-white",
    ghost: "invisible ms-2 inline-flex align-baseline",
    inline: cn("shrink-0", muted),
    none: "hidden",
  }[variant];
  return (
    <div
      aria-hidden={variant === "ghost" || undefined}
      className={cn(
        "items-center justify-end gap-1 text-[10px] leading-none",
        variant === "ghost" ? "" : "flex",
        tone,
      )}
    >
      {m.pinnedAt && !m.isDeleted ? (
        <Pin className="size-3 shrink-0" role="img" aria-label={t("pinned.badge")} />
      ) : null}
      {m.editedAt && !m.isDeleted ? <span>{t("labels.edited")}</span> : null}
      <span>{formatDate(new Date(m.createdAt), "p")}</span>
      {isMine && !m.isDeleted ? (
        m.sendState === "failed" ? (
          <AlertCircle className="size-3" />
        ) : tick === "pending" ? (
          <Clock3 className="size-3" role="img" aria-label={t("a11y.sending")} />
        ) : tick === "read" ? (
          // WhatsApp's scheme: one tick sent, two white delivered, two blue read.
          <CheckCheck
            className="size-3.5 text-chat-read"
            role="img"
            aria-label={t("a11y.read")}
          />
        ) : tick === "delivered" ? (
          <CheckCheck
            className="size-3.5 text-primary-foreground"
            role="img"
            aria-label={t("a11y.delivered")}
          />
        ) : (
          <Check className="size-3.5 text-primary-foreground" role="img" aria-label={t("a11y.sent")} />
        )
      ) : null}
    </div>
  );
};

const BubbleContent = (props: MessageBubbleProps) => {
  const { message: m, isMine, myUserId, onMessageUser } = props;
  const { t } = useTranslation("chat");
  // Pending bubbles can't open anyone yet: nothing about them is settled.
  const mentionProps = {
    mentions: m.metadata?.mentions,
    myUserId,
    onMention: m.id > 0 ? onMessageUser : undefined,
  };

  if (m.isDeleted) {
    return <p className="italic opacity-80">{t("labels.message_deleted")}</p>;
  }

  const caption = m.body?.trim() ? (
    <MessageText
      text={m.body}
      isMine={isMine}
      {...mentionProps}
      className={cn(MEDIA_TYPES.has(m.type) && "px-2 pt-1")}
    />
  ) : null;

  switch (m.type) {
    case "text":
      return (
        <MessageText
          text={m.body ?? ""}
          isMine={isMine}
          {...mentionProps}
          footer={<BubbleFooter message={m} isMine={isMine} tick={props.tick} variant="ghost" />}
        />
      );
    case "file":
      // Sent with "File": every attachment is a download row, photos and
      // videos included — only media messages show them inline.
      return (
        <div className="flex flex-col gap-1.5">
          {m.attachments.map((a) => (
            <FileCard key={a.fileId} attachment={a} isMine={isMine} />
          ))}
          {caption}
        </div>
      );
    case "image":
    case "video":
    case "voice":
      return (
        <div className="flex flex-col gap-1.5">
          <Attachments {...props} />
          {m.type !== "voice" ? caption : null}
        </div>
      );
    case "gif": {
      const url = m.metadata?.gif?.url;
      return url ? (
        <MediaImage url={url} onOpen={() => props.onOpenImage([url], 0)} className="w-72 max-w-full" />
      ) : (
        caption
      );
    }
    case "location":
      return <LocationCard message={m} isMine={isMine} />;
    case "contact":
      return <ContactCard {...props} />;
    case "poll":
      return m.poll ? <PollCard {...props} poll={m.poll} /> : caption;
    case "link":
      return <LinkCard {...props} />;
    case "shortcut":
      return <ShortcutCard message={m} isMine={isMine} />;
    default:
      return caption;
  }
};

const MediaImage = ({
  url,
  onOpen,
  className,
}: {
  url: string;
  onOpen: () => void;
  className?: string;
}) => (
  <button
    type="button"
    onClick={onOpen}
    className={cn("block overflow-hidden rounded-xl bg-muted", className)}
  >
    <img src={url} alt="" loading="lazy" className="max-h-80 w-full object-cover" />
  </button>
);

const Attachments = ({
  message: m,
  isMine,
  tick,
  myUserId,
  voicePlayed,
  onPlayed,
  onOpenImage,
}: MessageBubbleProps) => {
  const { t } = useTranslation("chat");
  const media = m.attachments.filter((a) => a.kind === "image" || a.kind === "video");
  const photos = media.filter((a) => a.kind === "image");
  const audio = m.attachments.filter((a) => a.kind === "audio");
  const files = m.attachments.filter((a) => a.kind === "file");
  const single = media.length === 1;
  return (
    <div className="flex flex-col gap-1.5">
      {media.length ? (
        <div className={cn("grid gap-1", single ? "w-72 max-w-full grid-cols-1" : "w-80 max-w-full grid-cols-2")}>
          {media.map((a) =>
            a.kind === "video" ? (
              <VideoTile
                key={a.fileId}
                url={a.fileUrl}
                className={cn("w-full rounded-xl", single ? "aspect-video max-h-80" : "aspect-square")}
              />
            ) : (
              <MediaImage
                key={a.fileId}
                url={a.fileUrl}
                // The viewer gets every photo of the message; videos play in place.
                onOpen={() => onOpenImage(photos.map((x) => x.fileUrl), photos.indexOf(a))}
                className={single ? undefined : "aspect-square [&_img]:h-full"}
              />
            ),
          )}
        </div>
      ) : null}
      {audio.map((a) => (
        <VoiceNotePlayer
          footer={<BubbleFooter message={m} isMine={isMine} tick={tick} variant="inline" />}
          key={a.fileId}
          url={a.fileUrl}
          durationMs={a.durationMs ?? m.metadata?.voice?.durationMs}
          waveform={m.metadata?.voice?.waveform}
          isMine={isMine}
          onPlay={() => onPlayed(m)}
          beforeDuration={
            isMine && m.id > 0 ? (
              // WhatsApp's mic: muted until everyone it was meant for played it.
              <Mic
                className={cn(
                  "size-3.5 shrink-0",
                  voicePlayed ? "text-chat-read" : "text-primary-foreground/60",
                )}
                role="img"
                aria-label={voicePlayed ? t("voice.played") : t("voice.not_played")}
              />
            ) : null
          }
          afterDuration={
            isUnplayedByMe(m, myUserId) ? (
              <span
                role="img"
                aria-label={t("voice.not_played")}
                className="size-2 shrink-0 rounded-full bg-primary"
              />
            ) : null
          }
        />
      ))}
      {files.map((a) => (
        <FileCard key={a.fileId} attachment={a} isMine={isMine} />
      ))}
    </div>
  );
};


const LocationCard = ({ message: m, isMine }: { message: IChatMessage; isMine: boolean }) => {
  const { t } = useTranslation("chat");
  const loc = m.metadata?.location;
  if (!loc) return null;
  // One card: the map on top, the place and "Open in Maps" under it.
  return (
    <RichCard
      isMine={isMine}
      href={mapsUrl(loc.latitude, loc.longitude)}
      top={<ChatLocationMap lat={loc.latitude} lng={loc.longitude} />}
      leading={
        <CardTile tone="danger" isMine={isMine}>
          <MapPin />
        </CardTile>
      }
      title={loc.name || loc.address || t("card.location")}
      subtitle={t("actions.open_in_maps")}
      action={
        <CardAction isMine={isMine}>
          <ArrowUpRight className="rtl-flip" />
        </CardAction>
      }
    />
  );
};

const ContactCard = ({ message: m, isMine, myUserId, onMessageUser }: MessageBubbleProps) => {
  const { t } = useTranslation("chat");
  const c = m.metadata?.contact;
  if (!c) return null;
  return (
    <div className="flex min-w-56 flex-col gap-2">
      <div className="flex items-center gap-2.5">
        <ChatAvatar name={c.name} imageUrl={c.profileImage} size="md" />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold">{c.name}</span>
          {c.phoneNumber ? (
            <span className={cn("truncate text-xs", isMine ? "opacity-75" : "text-muted-foreground")} dir="ltr">
              {c.phoneNumber}
            </span>
          ) : null}
          {c.email ? (
            <span className={cn("truncate text-xs", isMine ? "opacity-75" : "text-muted-foreground")}>{c.email}</span>
          ) : null}
        </div>
      </div>
      {c.userId && c.userId !== myUserId ? (
        <Button
          variant={isMine ? "secondary" : "outline"}
          size="xs"
          onClick={() => onMessageUser(c.userId!)}
        >
          <MessageCircle className="size-3.5" />
          {t("info.message")}
        </Button>
      ) : null}
    </div>
  );
};

/** Who voted for an option, at a glance: up to 3 overlapping faces, then +N. */
const VoterStack = ({
  ids,
  isMine,
  names,
  images,
}: {
  ids: number[];
  isMine: boolean;
  names: Map<number, string>;
  images?: Map<number, string | null>;
}) => {
  const { t } = useTranslation("chat");
  const shown = ids.slice(0, 3);
  const more = ids.length - shown.length;
  const ring = isMine ? "ring-primary" : "ring-background";
  return (
    <span className="flex shrink-0 items-center -space-x-1 rtl:space-x-reverse" aria-hidden>
      {shown.map((id) => (
        <ChatAvatar
          key={id}
          // One initial: two don't fit a face this small once they overlap.
          name={(names.get(id) ?? t("system.someone")).split(/\s+/)[0]}
          imageUrl={images?.get(id) ?? null}
          size="xs"
          className={cn("rounded-full ring-2", ring)}
        />
      ))}
      {more > 0 ? (
        <span
          className={cn(
            "relative flex size-6 items-center justify-center rounded-full text-[9px] font-semibold ring-2",
            ring,
            isMine ? "bg-primary-foreground/25" : "bg-muted text-muted-foreground",
          )}
        >
          +{more}
        </span>
      ) : null}
    </span>
  );
};

const PollCard = ({
  message: m,
  poll,
  isMine,
  myUserId,
  onVote,
  canSeeVoters,
  memberNames,
  memberImages,
}: MessageBubbleProps & { poll: IChatPoll }) => {
  const { t } = useTranslation("chat");
  const [votesOpen, setVotesOpen] = useState(false);
  const pending = m.id < 0;
  const showVotes = canSeeVoters && !pending && poll.totalVoters > 0;
  const muted = isMine ? "text-primary-foreground/75" : "text-muted-foreground";
  return (
    // Same design as the app's poll card: tile + question, then options as
    // rows with their bar underneath, then the total.
    <div className="flex w-64 max-w-full flex-col gap-3">
      {/* The header opens the full breakdown (who voted what), like "View votes". */}
      <button
        type="button"
        disabled={!showVotes}
        onClick={() => setVotesOpen(true)}
        aria-label={showVotes ? t("poll.view_votes") : undefined}
        className="-m-1 flex items-start gap-2.5 rounded-lg p-1 text-start enabled:hover:bg-black/5 disabled:cursor-default"
      >
        <span
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-[10px]",
            isMine ? "bg-primary-foreground/20" : "bg-primary-muted text-primary",
          )}
        >
          <ChartBarBig className="size-4" />
        </span>
        <div className="min-w-0">
          <p dir="auto" className="leading-snug font-semibold break-words">
            {poll.question}
          </p>
          <p className={cn("mt-0.5 text-xs", muted)}>
            {poll.allowMultiple ? t("poll.multiple") : t("poll.single")}
          </p>
        </div>
      </button>
      <div className="flex flex-col gap-1">
        {poll.options.map((o) => {
          const voted = myUserId != null && o.voterIds.includes(myUserId);
          const share = poll.totalVoters ? (o.voteCount / poll.totalVoters) * 100 : 0;
          return (
            <button
              key={o.id}
              type="button"
              disabled={pending}
              onClick={() => onVote(m, o.id)}
              aria-pressed={voted}
              className={cn(
                "-mx-1.5 flex flex-col gap-1.5 rounded-lg px-1.5 py-1.5 text-start transition-colors disabled:cursor-default",
                isMine ? "hover:bg-primary-foreground/10" : "hover:bg-muted",
              )}
            >
              <span className="flex items-center gap-2.5">
                <span
                  className={cn(
                    "flex size-5 shrink-0 items-center justify-center border-2 transition-colors",
                    poll.allowMultiple ? "rounded-[5px]" : "rounded-full",
                    voted
                      ? isMine
                        ? "border-primary-foreground bg-primary-foreground text-primary"
                        : "border-primary bg-primary text-primary-foreground"
                      : isMine
                        ? "border-primary-foreground/60"
                        : "border-border-strong",
                  )}
                >
                  {voted ? <Check className="size-3" strokeWidth={3} /> : null}
                </span>
                <span dir="auto" className="flex-1 text-start text-sm break-words">
                  {o.text}
                </span>
                {canSeeVoters && o.voterIds.length ? (
                  <VoterStack
                    ids={o.voterIds}
                    isMine={isMine}
                    names={memberNames}
                    images={memberImages}
                  />
                ) : null}
                <span className={cn("text-xs font-semibold tabular-nums", muted)}>
                  {o.voteCount}
                </span>
              </span>
              {/* The bar lines up under the option text. */}
              <span
                className={cn(
                  "ms-7.5 h-1.5 overflow-hidden rounded-full",
                  isMine ? "bg-primary-foreground/20" : "bg-primary/10",
                )}
              >
                <span
                  className={cn(
                    "block h-full rounded-full transition-[width] duration-300",
                    isMine ? "bg-primary-foreground" : "bg-primary",
                  )}
                  style={{ width: `${share}%` }}
                />
              </span>
            </button>
          );
        })}
      </div>
      <p
        className={cn(
          "border-t pt-2 text-xs",
          isMine ? "border-primary-foreground/20" : "border-border",
          muted,
        )}
      >
        {t("poll.votes", { count: poll.totalVoters })}
      </p>
      {showVotes ? (
        <>
          <Button
            variant="link"
            size="xs"
            onClick={() => setVotesOpen(true)}
            className={cn(
              "-mt-1.5 self-center text-xs font-semibold",
              isMine && "text-primary-foreground",
            )}
          >
            {t("poll.view_votes")}
          </Button>
          <PollVotesDialog
            open={votesOpen}
            onOpenChange={setVotesOpen}
            poll={poll}
            myUserId={myUserId}
            memberNames={memberNames}
            memberImages={memberImages}
          />
        </>
      ) : null}
    </div>
  );
};

const LinkCard = ({
  message: m,
  isMine,
  myUserId,
  onMessageUser,
}: Pick<MessageBubbleProps, "message" | "isMine" | "myUserId" | "onMessageUser">) => {
  const link = m.metadata?.link;
  if (!link) return null;
  let host = link.url;
  try {
    host = new URL(link.url).hostname.replace(/^www\./, "");
  } catch {
    // keep the raw url
  }
  return (
    <div className="flex w-64 max-w-full flex-col gap-1.5">
      <RichCard
        isMine={isMine}
        href={link.url}
        leading={
          <CardTile tone="info" isMine={isMine}>
            <Globe />
          </CardTile>
        }
        title={link.title || host}
        subtitle={link.title ? host : link.url}
        subtitleLtr
        action={
          <CardAction isMine={isMine}>
            <ArrowUpRight className="rtl-flip" />
          </CardAction>
        }
      />
      {m.body?.trim() ? (
        <MessageText
          text={m.body}
          isMine={isMine}
          mentions={m.metadata?.mentions}
          myUserId={myUserId}
          onMention={m.id > 0 ? onMessageUser : undefined}
          className="px-0.5"
        />
      ) : null}
    </div>
  );
};

